import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import axios from '../../../services/axios';
import Modal from '../../../components/Modal/Modal';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';

// =============================================================================
// Estilos
// =============================================================================

const StatusPill = styled.span`
   display: inline-flex;
   align-items: center;
   padding: 2px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   border: 1px solid ${({ $borderColor }) => $borderColor ?? 'rgba(0,0,0,0.15)'};
   background: ${({ $backgroundColor }) => $backgroundColor ?? 'rgba(0,0,0,0.06)'};
   color: ${({ $textColor }) => $textColor ?? '#444'};
`;

const DetailHeader = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 16px;
   padding: 0 0 16px;
   border-bottom: 1px solid rgba(0,0,0,0.07);
   margin-bottom: 20px;
`;

const DetailItem = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
`;

const DetailLabel = styled.span`
   font-size: 0.7rem;
   color: #9ca3af;
   text-transform: uppercase;
   letter-spacing: 0.03em;
`;

const DetailValue = styled.span`
   font-size: 0.875rem;
   font-weight: 500;
   color: #111827;
`;

// =============================================================================
// Constantes
// =============================================================================

const VOUCHER_TYPE_LABEL = {
   VALE_ALIMENTACAO: 'Vale Alimentação',
   VALE_TRANSPORTE:  'Vale Transporte',
};

const VOUCHER_STATUS_CONFIG = {
   PENDENTE: { label: 'Pendente', bg: 'rgba(255,193,7,0.15)',  border: 'rgba(255,193,7,0.50)',  text: '#7d5a00' },
   PAGO:     { label: 'Pago',     bg: 'rgba(46,125,50,0.12)',  border: 'rgba(46,125,50,0.35)',  text: '#1b5e20' },
};

// =============================================================================
// Helpers
// =============================================================================

function formatCurrencyBRL(value) {
   if (value === null || value === undefined) return '—';
   return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatMonthBR(isoDateString) {
   if (!isoDateString) return '—';
   const [year, month] = String(isoDateString).substring(0, 10).split('-');
   return `${month}/${year}`;
}

function formatDiscountDescription(companyPaidAmount, calculationType, calculationValue, discountAmount) {
   if (calculationType === 'VALOR_FIXO') {
      return `Valor fixo: ${formatCurrencyBRL(discountAmount)}`;
   }
   return `Total: ${formatCurrencyBRL(companyPaidAmount)} (-${Number(calculationValue).toFixed(2)}%) → ${formatCurrencyBRL(discountAmount)}`;
}

// =============================================================================
// Componente
// =============================================================================

/**
 * Modal para visualizar, editar e excluir um benefício VA/VT existente.
 * Recebe o objeto voucher completo diretamente — não faz fetch próprio.
 *
 * Props:
 *   isOpen       {boolean}  — controla visibilidade
 *   onClose      {fn}       — chamado ao fechar
 *   onSuccess    {fn}       — chamado após edição ou exclusão bem-sucedida
 *   voucher      {object}   — objeto do benefício com todos os campos
 *   employeeId   {number}   — ID do funcionário dono do benefício
 *   employeeName {string}   — nome do funcionário para exibição no título
 */
export default function BenefitVoucherDetailModal({
   isOpen,
   onClose,
   onSuccess,
   voucherId,
   employeeId,
   employeeName,
}) {
   // ─── Voucher completo carregado via GET ────────────────────────
   const [voucher, setVoucher]         = useState(null);
   const [isLoadingVoucher, setIsLoadingVoucher] = useState(false);

   // ─── Modo de visualização ou edição ───────────────────────────
   const [isEditMode, setIsEditMode]           = useState(false);
   const [editFormData, setEditFormData]       = useState({});
   const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

   // ─── Modal de confirmação de exclusão ─────────────────────────
   const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
   const [isDeleting, setIsDeleting]                   = useState(false);

   // ─── Buscar voucher completo ao abrir ─────────────────────────
   const fetchVoucher = useCallback(async () => {
      if (!isOpen || !employeeId || !voucherId) return;
      setIsLoadingVoucher(true);
      try {
         const response = await axios.get(
            `/hr/employees/${employeeId}/benefit-vouchers`,
         );
         const vouchers = response.data?.data?.benefit_vouchers ?? [];
         const found = vouchers.find((v) => v.id === voucherId);
         setVoucher(found ?? null);
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.message ??
            'Erro ao carregar benefício.';
         toast.error(errorMessage);
      } finally {
         setIsLoadingVoucher(false);
      }
   }, [isOpen, employeeId, voucherId]);

   useEffect(() => {
      fetchVoucher();
   }, [fetchVoucher]);

   // ─── Abrir modo de edição ─────────────────────────────────────
   const handleOpenEditMode = () => {
      setEditFormData({
         company_paid_amount:        voucher.company_paid_amount,
         discount_calculation_type:  voucher.discount_calculation_type,
         discount_calculation_value: voucher.discount_calculation_value,
         voucher_status:             voucher.voucher_status,
         notes:                      voucher.notes ?? '',
      });
      setIsEditMode(true);
   };

   // ─── Fechar modal principal e resetar estado ──────────────────
   const handleClose = useCallback(() => {
      setIsEditMode(false);
      setEditFormData({});
      setIsDeletionModalOpen(false);
      setVoucher(null);
      onClose();
   }, [onClose]);

   const handleCancelEdit = () => {
      setIsEditMode(false);
      setEditFormData({});
   };

   // ─── Handler de campo da edição ───────────────────────────────
   const handleEditFieldChange = (event) => {
      const { name, value } = event.target;
      setEditFormData((previous) => ({ ...previous, [name]: value }));
   };

   // ─── Salvar edição ────────────────────────────────────────────
   const handleEditSubmit = async () => {
      setIsSubmittingEdit(true);
      try {
         await axios.patch(
            `/hr/employees/${employeeId}/benefit-vouchers/${voucherId}`,
            {
               company_paid_amount:        Number(editFormData.company_paid_amount),
               discount_calculation_type:  editFormData.discount_calculation_type,
               discount_calculation_value: Number(editFormData.discount_calculation_value),
               voucher_status:             editFormData.voucher_status,
               notes:                      editFormData.notes || null,
            },
         );

         toast.success('Benefício atualizado com sucesso!');
         setIsEditMode(false);
         fetchVoucher();
         onSuccess?.();
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao atualizar benefício.';
         toast.error(errorMessage);
      } finally {
         setIsSubmittingEdit(false);
      }
   };

   // ─── Confirmar exclusão ────────────────────────────────────────
   const handleConfirmDeletion = async () => {
      setIsDeleting(true);
      try {
         await axios.delete(
            `/hr/employees/${employeeId}/benefit-vouchers/${voucherId}`,
         );

         toast.success('Benefício cancelado com sucesso.');
         setIsDeletionModalOpen(false);
         handleClose();
         onSuccess?.();
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao cancelar benefício.';
         toast.error(errorMessage);
      } finally {
         setIsDeleting(false);
      }
   };

   if (!voucherId) return null;

   const voucherTypeLabel = voucher
      ? VOUCHER_TYPE_LABEL[voucher.voucher_type] ?? voucher.voucher_type
      : '...';
   const statusConfig  = voucher ? VOUCHER_STATUS_CONFIG[voucher.voucher_status] ?? { label: voucher.voucher_status } : {};
   const modalTitle    = `${voucherTypeLabel} — ${employeeName ?? ''}`;

   return (
      <>
         <Modal isOpen={isOpen} title={modalTitle} onClose={handleClose} maxWidth="520px">

            {isLoadingVoucher && (
               <p style={{ fontSize: '13px', color: '#888' }}>Carregando...</p>
            )}

            {/* ─── Modo visualização ─── */}
            {!isLoadingVoucher && voucher && !isEditMode && (
               <>
                  <DetailHeader>
                     <DetailItem>
                        <DetailLabel>Mês de referência</DetailLabel>
                        <DetailValue>{formatMonthBR(voucher.reference_month)}</DetailValue>
                     </DetailItem>

                     <DetailItem>
                        <DetailLabel>Valor pago pela empresa</DetailLabel>
                        <DetailValue>{formatCurrencyBRL(voucher.company_paid_amount)}</DetailValue>
                     </DetailItem>

                     <DetailItem>
                        <DetailLabel>Desconto do funcionário</DetailLabel>
                        <DetailValue>{formatCurrencyBRL(voucher.employee_discount_amount)}</DetailValue>
                     </DetailItem>

                     <DetailItem>
                        <DetailLabel>Cálculo</DetailLabel>
                        <DetailValue>
                           {formatDiscountDescription(
                              voucher.company_paid_amount,
                              voucher.discount_calculation_type,
                              voucher.discount_calculation_value,
                              voucher.employee_discount_amount,
                           )}
                        </DetailValue>
                     </DetailItem>

                     <DetailItem>
                        <DetailLabel>Status</DetailLabel>
                        <StatusPill
                           $backgroundColor={statusConfig.bg}
                           $borderColor={statusConfig.border}
                           $textColor={statusConfig.text}
                        >
                           {statusConfig.label}
                        </StatusPill>
                     </DetailItem>

                     {voucher.notes && (
                        <DetailItem>
                           <DetailLabel>Observações</DetailLabel>
                           <DetailValue>{voucher.notes}</DetailValue>
                        </DetailItem>
                     )}
                  </DetailHeader>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                     <button
                        type="button"
                        onClick={() => setIsDeletionModalOpen(true)}
                     >
                        Cancelar débito
                     </button>
                     <button type="button" onClick={handleOpenEditMode}>
                        Editar
                     </button>
                  </div>
               </>
            )}

            {/* ─── Modo edição ─── */}
            {!isLoadingVoucher && voucher && isEditMode && (
               <FormGrid $cols={12} $colsMobile={12}>
                  <Field $col={6} $colMobile={12}>
                     <label htmlFor="bvd-company-paid-amount">Valor pago pela empresa (R$)</label>
                     <input
                        id="bvd-company-paid-amount"
                        type="number"
                        name="company_paid_amount"
                        value={editFormData.company_paid_amount}
                        onChange={handleEditFieldChange}
                        step="0.01"
                        min="0"
                     />
                  </Field>

                  <Field $col={6} $colMobile={12}>
                     <label htmlFor="bvd-voucher-status">Status</label>
                     <select
                        id="bvd-voucher-status"
                        name="voucher_status"
                        value={editFormData.voucher_status}
                        onChange={handleEditFieldChange}
                     >
                        <option value="PENDENTE">Pendente</option>
                        <option value="PAGO">Pago</option>
                     </select>
                  </Field>

                  <Field $col={6} $colMobile={12}>
                     <label htmlFor="bvd-discount-type">Tipo de cálculo do desconto</label>
                     <select
                        id="bvd-discount-type"
                        name="discount_calculation_type"
                        value={editFormData.discount_calculation_type}
                        onChange={handleEditFieldChange}
                     >
                        <option value="PERCENTUAL_VALOR_PAGO">% do valor pago</option>
                        <option value="PERCENTUAL_SALARIO_BASE">% do salário base</option>
                        <option value="VALOR_FIXO">Valor fixo (R$)</option>
                     </select>
                  </Field>

                  <Field $col={6} $colMobile={12}>
                     <label htmlFor="bvd-discount-value">
                        {editFormData.discount_calculation_type === 'VALOR_FIXO'
                           ? 'Valor fixo (R$)'
                           : 'Percentual (%)'}
                     </label>
                     <input
                        id="bvd-discount-value"
                        type="number"
                        name="discount_calculation_value"
                        value={editFormData.discount_calculation_value}
                        onChange={handleEditFieldChange}
                        step="0.01"
                        min="0"
                     />
                  </Field>

                  <Field $col={12} $colMobile={12}>
                     <label htmlFor="bvd-notes">Observações</label>
                     <input
                        id="bvd-notes"
                        type="text"
                        name="notes"
                        value={editFormData.notes}
                        onChange={handleEditFieldChange}
                        placeholder="Observações opcionais..."
                     />
                  </Field>

                  <Field $col={12} $colMobile={12}>
                     <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '4px' }}>
                        <button type="button" onClick={handleCancelEdit} disabled={isSubmittingEdit}>
                           Voltar
                        </button>
                        <button type="button" onClick={handleEditSubmit} disabled={isSubmittingEdit}>
                           {isSubmittingEdit ? 'Salvando...' : 'Salvar alterações'}
                        </button>
                     </div>
                  </Field>
               </FormGrid>
            )}
         </Modal>

         {/* ─── Modal de confirmação de exclusão ─────────────────── */}
         <Modal
            isOpen={isDeletionModalOpen}
            title="Confirmar exclusão"
            onClose={() => setIsDeletionModalOpen(false)}
            maxWidth="420px"
         >
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#374151' }}>
               Tem certeza que deseja excluir o registro de{' '}
               <strong>{voucherTypeLabel}</strong> de{' '}
               <strong>{voucher ? formatMonthBR(voucher.reference_month) : '—'}</strong>?
            </p>
            <p style={{ margin: '8px 0 0', fontSize: '0.8rem', color: '#9ca3af' }}>
               Esta ação não pode ser desfeita.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
               <button
                  type="button"
                  onClick={() => setIsDeletionModalOpen(false)}
                  disabled={isDeleting}
               >
                  Cancelar
               </button>
               <button type="button" onClick={handleConfirmDeletion} disabled={isDeleting}>
                  {isDeleting ? 'Excluindo...' : 'Confirmar exclusão'}
               </button>
            </div>
         </Modal>
      </>
   );
}

BenefitVoucherDetailModal.propTypes = {
   isOpen:       PropTypes.bool.isRequired,
   onClose:      PropTypes.func.isRequired,
   onSuccess:    PropTypes.func,
   voucherId:    PropTypes.number.isRequired,
   employeeId:   PropTypes.number.isRequired,
   employeeName: PropTypes.string,
};
